import { useAuthStore } from '@/stores/authStore.js'

/**
 * Internal tenant object that defines tenant info, settings and permissions based on host.
 */
export class Tenant {
	/**
	 * Create a Tenant object.
	 * @param {number} id
	 * @param {string[]} name Tenant Name
	 * @param {Object} hosts
	 * @param {Object} registrationLogo
	 * @param {Object} sidebarLogoLarge
	 * @param {Object} sidebarLogoSmall
	 * @param {Array<string>} hero
	 * @param {Array<string>} comingSoonRoutes Array of route names that should show the coming soon screen. Use route names.
	 * @param {{terms: string, privacy: string}} termsAndPrivacy containing the urls for the Terms and Privacy links
	 * @param {string} favIconPath Favicon file name, place these in the public folder. Comagine left as "favicon.ico" as default.
	 */
	constructor(id, name, hosts, registrationLogo, sidebarLogoLarge, sidebarLogoSmall, hero, comingSoonRoutes, termsAndPrivacy, favIconPath) {
		this.id = id;
		this.name = name;
		this.hosts = hosts;
		this.registrationLogo = registrationLogo;
		this.sidebarLogoLarge = sidebarLogoLarge;
		this.sidebarLogoSmall = sidebarLogoSmall;
		this.hero = hero;
		this.comingSoonRoutes = comingSoonRoutes;
		this.termsAndPrivacy = termsAndPrivacy;
		this.favIconPath = favIconPath;
	}

	/**
	 * Returns true if the route under development for the current host
	 * @param route
	 * @returns {boolean}
	 */
	isRouteComingSoon(route) {
		return this.comingSoonRoutes.indexOf(route) >= 0;
	}
}

/**
 * Collection of Tenants
 */
export class Tenants {

	static EQRO = 1
	static AIAN = 2
	static ESRD = 3
	static CARE = 4
	static TEST = 5

	/**
	 * Array of development hosts names for showing/changing website for development purposes
	 * @type {string[]} Hostnames
	 */
	static devHosts = [
		"localhost", "127.0.0.1",
		"local.comagine.org",
		"local.aianqio.org",
		"local.esrdnw.org",
		"local.care.org",
		"local.zoo.org",
		"dev-portal.comagine.org",
		"dev-portal.aianqio.org",
		"dev-portal.esrdnw.org",
		"dev-providerportal.comagine.org",
		"dev-portal.zoo.org"
	];

	/**
	 * Array of application tenants
	 * @type {[Tenant]}
	 */
	static tenants = [
		new Tenant(
			Tenants.EQRO,
			"Comagine Health Platform",
			["localhost", "local.comagine.org", "dev-portal.comagine.org", "uat-portal.comagine.org", "sit-portal.comagine.org", "portal.comagine.org"],
			{
				image: "comagine/registration-logo.svg"
			},
			{
				image: "comagine/sidebar-logo-large.svg"
			},
			{
				image: "comagine/sidebar-logo-small.png"
			},
			{
				image: "comagine/hero.png",
				background: "bg-comagineLogoGradient",
				classData: "right-0 bottom-0"
			},
			[],
			{
				terms: "https://app.termly.io/document/privacy-policy/c40363e1-5d6e-420c-841f-d5fcf2cfe933",
				privacy: "https://app.termly.io/document/privacy-policy/e5c0f265-1fb7-4f10-8c63-29cdf1b4af04"
			},
			'favicon.ico' //See public folder
		),
		new Tenant(
			Tenants.AIAN,
			"AIAN QIO Portal",
			["127.0.0.1", "local.aianqio.org", "dev-portal.aianqio.org", "uat-portal.aianqio.org", "sit-portal.aianqio.org", "portal.aianqio.org"],
			{
				image: "aianqio/registration-logo.svg"
			},
			{
				image: "aianqio/sidebar-logo-large.svg"
			},
			{
				image: "aianqio/sidebar-logo-small.png"
			},
			{
				image: "aianqio/hero.jpg",
				background: "bg-neutral",
				classData: "h-screen object-cover w-full"
			},
			['learning-center'],
			{
				terms: "https://app.termly.io/document/privacy-policy/c40363e1-5d6e-420c-841f-d5fcf2cfe933",
				privacy: "https://app.termly.io/document/privacy-policy/e5c0f265-1fb7-4f10-8c63-29cdf1b4af04"
			},
			'AIAN_favicon_16.svg' //See public folder
		),
		new Tenant(
			Tenants.ESRD,
			"ESRD Portal",
			["local.esrdnw.org", "dev-portal.esrdnw.org", "uat-portal.esrdnw.org", "sit-portal.esrdnw.org", "portal.esrdnw.org"],
			{
				image: "esrd/registration-logo.svg"
			},
			{
				image: "esrd/sidebar-logo-large.svg"
			},
			{
				image: "esrd/sidebar-logo-small.png"
			},
			{
				image: "esrd/hero.png",
				background: "bg-comagineLogoGradient",
				classData: "right-0 bottom-0"
			},
			[],
			{
				terms: "https://app.termly.io/document/privacy-policy/c40363e1-5d6e-420c-841f-d5fcf2cfe933",
				privacy: "https://app.termly.io/document/privacy-policy/e5c0f265-1fb7-4f10-8c63-29cdf1b4af04"
			},
			'favicon.ico' //See public folder
		),
		new Tenant(
			Tenants.CARE,
			"Care Management Portal",
			["local.care.org", "dev-providerportal.comagine.org", "uat-providerportal.comagine.org", "sit-providerportal.comagine.org", "providerportal.comagine.org"],
			{
				image: "comagine/registration-logo.svg"
			},
			{
				image: "comagine/sidebar-logo-large.svg"
			},
			{
				image: "comagine/sidebar-logo-small.png"
			},
			{
				image: "comagine/hero.png",
				background: "bg-comagineLogoGradient",
				classData: "right-0 bottom-0"
			},
			[],
			{
				terms: "https://app.termly.io/document/privacy-policy/c40363e1-5d6e-420c-841f-d5fcf2cfe933",
				privacy: "https://app.termly.io/document/privacy-policy/e5c0f265-1fb7-4f10-8c63-29cdf1b4af04"
			},
			'favicon.ico' //See public folder
		),
		new Tenant(
			Tenants.TEST,
			"Zoo Portal",
			["localhost", "local.zoo.org", "dev-zoo.comagine.org", "uat-zoo.comagine.org", "sit-zoo.comagine.org"],
			{
				image: "comagine/registration-logo.svg"
			},
			{
				image: "zoo/zoo.png"
			},
			{
				image: "zoo/zoo.png"
			},
			{
				image: "comagine/hero.png",
				background: "bg-comagineLogoGradient",
				classData: "right-0 bottom-0"
			},
			[],
			{
				terms: "https://app.termly.io/document/privacy-policy/c40363e1-5d6e-420c-841f-d5fcf2cfe933",
				privacy: "https://app.termly.io/document/privacy-policy/e5c0f265-1fb7-4f10-8c63-29cdf1b4af04"
			},
			'favicon.ico' //See public folder
		),
	]

	/**
	 * Gets a Tenant object based on a tenants id
	 * @param id Tenant id
	 * @returns {Tenant}
	 */
	static getTenantById(id) {
		return this.tenants.find(tenant => tenant.id === id)
	}

	/**
	 * Gets a Tenant object based on window.location.hostname
	 * @returns {Tenant}
	 */
	static getTenant() {
		return this.tenants.find(tenant => tenant.hosts.includes(window.location.hostname))
	}

	/**
	 * Gets a Tenant object based on a tenants hostname
	 * @param {string} hostname Hostname (window.location.hostname)
	 * @returns {Tenant}
	 */
	static getTenantByHostname(hostname) {
		return this.tenants.find(tenant => tenant.hosts.includes(hostname))
	}

	/**
	 * Returns true if application name for the current tenant.
	 * @returns {string}
	 */
	static getTenantAppName() {
		const tenant = this.getTenantByHostname(window.location.hostname)

		if (tenant && tenant['name'])
		{
			return tenant.name;
		}

		return "Portal";
	}

	/**
	 * Returns true if application name for the current tenant.
	 * @returns {string}
	 */
	static getFavIconPath() {
		const tenant = this.getTenantByHostname(window.location.hostname)

		if (tenant && tenant['favIconPath'])
		{
			return tenant.favIconPath;
		}

		return "favicon.ico";
	}

	/**
	 * Returns true if the current hostname is used for development
	 * @returns {boolean}
	 */
	static isDevHost() {
		return this.devHosts.indexOf(window.location.hostname) > -1;
	}

	/**
	 * Returns the enabled status of the given module (based on module route, i.e. `data.submit-data`).
	 * Checks the tenant's module settings that come from the backend. If there is no specific module
	 * setting for the given route, assume it is enabled.
	 *
	 * Requires the user to be logged in.
	 *
	 * @param moduleRoute
	 * @returns {boolean}
	 */
	static isModuleEnabled(moduleRoute) {
		const settingsJson = useAuthStore().getAuth?.tenantInfo()?.settings_json
		if (!settingsJson) return true

		const route = JSON.parse(settingsJson).modules.value.find((module) => {
			return module.route === moduleRoute
		})
		return !route || route.enabled
	}
}